import React, { useMemo } from 'react';
import { Check, Copy, Plus } from 'lucide-react';
import { useComponentCardContext } from '../../contexts/ComponentCardContext';
import { calculateCurrentCondition } from '../../../../../Helpers/conditionUtils';
import { getLifetimeForDirection } from '../../../TemplatesEditor/types';
import { resolveCardinalDirection } from '../../utils/cardinalDirectionResolver';
import { CostSourceLegend } from '../../../components/CostSourceChip';
import { MIXED_VALUE, unanimousOrNull } from '../../utils/bulkSelection';
import type { ComponentWithReplacement } from '../../types';
import type { Building } from '../../../../../types/protocols';
import { isTemplateArchived } from '../../../../../Helpers/maintenanceTemplates';
import {
    ComponentHeader,
    ComponentImages,
    ActivityHeaders,
    ReplacementRow,
    MaintenanceRow,
    ComponentTimeline,
    CheckRows,
    BulkSelectionHeader
} from './index';

/** Massredigering: the selection actions the bulk header offers */
export interface BulkCardActions {
    unselectedOfType: number;
    onSelectAllOfType: () => void;
    onClearSelection: () => void;
}

interface ComponentCardProps {
    /** Given in Massredigering, where the card edits every selected component */
    bulkActions?: BulkCardActions;
}

// Today's condition: the assessment degraded over the directional lifetime, as in ConditionModal
const currentConditionOf = (plan: ComponentWithReplacement, building: Building | null): number | null => {
    const condition = plan.component.condition;
    const assessed = plan.hasCondition ? condition?.value?.value ?? null : null;
    if (assessed === null) return null;
    if (!plan.lifetime || !condition?.updatedWhen) return assessed;
    const direction = resolveCardinalDirection(plan.component, building);
    const expectedLifetime = getLifetimeForDirection(plan.lifetime, direction).expectedLifetimeYears;
    if (expectedLifetime == null || expectedLifetime <= 0) return assessed;
    return calculateCurrentCondition(assessed, condition.updatedWhen, expectedLifetime) ?? assessed;
};

/**
 * Main ComponentCard wrapper - consumes all data from ComponentCardContext
 * Orchestrates all component card sub-components and handles conditional rendering
 *
 * Note: This component must be wrapped in ComponentCardProvider to function.
 * The provider binds all handlers and display helpers to the selected component,
 * or in Massredigering to every selected component at once: the card then has a
 * selection header instead of the component's own, no images or timeline, and
 * rows that read "Blandade" where the components disagree.
 */
export const ComponentCard: React.FC<ComponentCardProps> = ({ bulkActions }) => {
    // All data and handlers come from context
    const {
        componentKey,
        plan,
        bulk,
        buildingKey,
        building,
        templates,
        lifetime,
        draftComponents,
        isDone,
        isExcluded,
        isReplacementYearInvalid,
        getMaintenanceDateInvalid,
        getDisplayReplacementYear,
        getDisplayReplacementCost,
        replacementQuantity,
        replacementUnitCount,
        onReplacementUnitCountChange,
        onReplacementUnitCountClear,
        getDisplayLifetime,
        getDisplayMaintenanceInterval,
        getDisplayMaintenanceDate,
        getDisplayMaintenanceCost,
        getDisplayIntervalType,
        onOpenConditionModal,
        onOpenLifetimeModal,
        onReplacementYearChange,
        onReplacementYearBlur,
        onMaintenanceRemove,
        onAddRowToAll,
        onMaintenanceDateChange,
        onMaintenanceDateBlur,
        onOpenNewActivityModal,
        onOpenCopyActivitiesModal,
        hasCopyActivitiesSources,
        onDoneToggle,
        onOpenCostSettingsModal,
        onOpenReplacementCostModal,
        onOpenIntervalSettingsModal,
        onOpenEditTemplateModal,
        onOpenAreaModal,
        highlightedActivity,
        setHighlightedActivity,
        isDueThisYear,
        calculateNextMaintenanceDate,
        getReplacementCostExplanation,
        getMaintenanceCostExplanation,
        onQuantityChange,
        onQuantityClear,
        getActivityCustomQuantity,
        getActivityComment,
        hasActivityEntry,
        onCommentChange,
        getDisplayArea,
        getComponentQuantity,
        getActivityHasCustomCost,
        getActivityCostBasis,
        replacementCostBasis,
        getTemplateCostUnit,
        coveringChecks,
        onOpenAddCheck,
        onRemoveCheck,
        onEditCheckDate
    } = useComponentCardContext();

    const plans = useMemo(() => bulk?.plans ?? [plan], [bulk, plan]);

    // Calculate area for display (memoized)
    const displayArea = useMemo(() => {
        const totalArea = building ? plan.component.totalArea(building) : null;
        return totalArea !== null
            ? `${totalArea.toLocaleString('sv-SE')} m²`
            : '-';
    }, [building, plan.component]);

    const currentCondition = useMemo(() => currentConditionOf(plan, building), [plan, building]);

    // Massredigering: the skick and years the selection agrees on, else "Blandade"
    const commonCondition = useMemo(
        () => unanimousOrNull(plans.map(p => currentConditionOf(p, building)?.toFixed(1) ?? null)),
        [plans, building]
    );
    const yearsRemainingLabel = useMemo(() => {
        if (!bulk) return undefined;
        const years = plans.map(p => (p.yearsRemaining !== null ? Math.max(0, Math.floor(p.yearsRemaining)) : null));
        const shared = unanimousOrNull(years);
        return shared === null ? MIXED_VALUE : `${shared} år kvar`;
    }, [bulk, plans]);

    // Determine what to show
    const allComplete = plans.every(p => p.hasCondition && p.hasLifetime);
    const hasReplacement = allComplete && plans.every(p => p.replacementDate && p.yearsRemaining !== null);
    const hasMaintenance = Array.isArray(templates) && templates.length > 0 && lifetime;
    // A component whose rows were all removed keeps the table, so "Ny aktivitet" can put one back
    const hasRemovedActivities = plans.some(p => Object.keys(draftComponents[p.component.key]?.removedActivities ?? {}).length > 0);
    const hasActivities = hasReplacement || hasMaintenance || hasRemovedActivities || coveringChecks.length > 0;
    // Massredigering: activities wait until every selected component has skick and livslängd
    const showActivities = hasActivities && (!bulk || allComplete);
    const hasTimeline = !bulk && lifetime && plan.hasCondition && plan.hasLifetime;
    const hasPhotos = plan.component.photos && Object.keys(plan.component.photos).length > 0;
    const doneLabel = bulk
        ? (isDone ? 'Klara (klicka för att ta bort)' : `Markera ${bulk.keys.length} som klara`)
        : (isDone ? 'Klar (klicka för att ta bort)' : 'Markera som klar');

    return (
        <div className={`component-container ${isDone ? 'done' : ''} ${isExcluded ? 'excluded' : ''}`} key={componentKey}>
            {/* Component Header */}
            {bulk && bulkActions ? (
                <BulkSelectionHeader
                    plans={plans}
                    building={building}
                    incompletePlans={bulk.incompletePlans}
                    commonCondition={commonCondition === null ? null : parseFloat(commonCondition)}
                    unselectedOfType={bulkActions.unselectedOfType}
                    onSelectAllOfType={bulkActions.onSelectAllOfType}
                    onClearSelection={bulkActions.onClearSelection}
                    onOpenConditionModal={onOpenConditionModal}
                    disabled={isExcluded}
                />
            ) : (
                <ComponentHeader
                    component={plan.component}
                    buildingKey={buildingKey}
                    building={building}
                    hasLifetime={plan.hasLifetime}
                    displayArea={displayArea}
                    currentCondition={currentCondition}
                    yearsRemaining={plan.yearsRemaining}
                    onOpenLifetime={onOpenLifetimeModal}
                    onOpenAreaModal={onOpenAreaModal}
                    onOpenConditionModal={onOpenConditionModal}
                    disabled={isExcluded}
                />
            )}

            {/* Component Images and Locations */}
            {!bulk && (hasPhotos || plan.component.photoLocations || plan.component.blueprintLocations || plan.component.mapLocations) && (
                <ComponentImages
                    photos={plan.component.photos || {}}
                    buildingKey={buildingKey}
                    building={building}
                    photoLocations={plan.component.photoLocations}
                    blueprintLocations={plan.component.blueprintLocations}
                    mapLocations={plan.component.mapLocations}
                />
            )}

            {/* Activity Table */}
            {showActivities && (
                <>
                    <CostSourceLegend className="px-2 pb-2" />
                    <ActivityHeaders />

                    {/* Replacement Row */}
                    {hasReplacement && (
                        <ReplacementRow
                            lifetimeYears={lifetime ? getDisplayLifetime : 'N/A'}
                            yearsRemaining={plan.yearsRemaining}
                            yearsRemainingLabel={yearsRemainingLabel}
                            quantity={replacementQuantity}
                            unitCount={!bulk && lifetime?.costUnit === 'per_unit' ? {
                                value: replacementUnitCount,
                                onChange: onReplacementUnitCountChange,
                                onClear: onReplacementUnitCountClear
                            } : undefined}
                            displayCost={getDisplayReplacementCost}
                            costExplanation={getReplacementCostExplanation}
                            costBasis={replacementCostBasis}
                            isHighlighted={highlightedActivity === `${componentKey}-replacement`}
                            isOverdue={plans.some(p => p.yearsRemaining !== null && p.yearsRemaining <= 0)}
                            onMouseEnter={() => setHighlightedActivity(`${componentKey}-replacement`)}
                            onMouseLeave={() => setHighlightedActivity(null)}
                            onOpenReplacementCostModal={onOpenReplacementCostModal}
                            onOpenConditionModal={onOpenConditionModal}
                            disabled={isExcluded}
                        />
                    )}

                    {/* Maintenance Rows */}
                    {hasMaintenance && templates.map((template) => {
                        const nextDate = calculateNextMaintenanceDate(template.id);
                        const isOverdue = !isExcluded && isDueThisYear(nextDate);

                        return (
                            <MaintenanceRow
                                key={`${componentKey}-${template.id}`}
                                templateName={template.name}
                                isTemplateArchived={isTemplateArchived(template)}
                                isDisabled={isExcluded}
                                isOverdue={isOverdue}
                                isHighlighted={highlightedActivity === `${componentKey}-${template.id}`}
                                displayInterval={getDisplayMaintenanceInterval(template.id)}
                                displayDate={getDisplayMaintenanceDate(template.id)}
                                displayCost={getDisplayMaintenanceCost(template.id)}
                                costExplanation={getMaintenanceCostExplanation(template.id)}
                                costBasis={getActivityCostBasis(template.id)}
                                displayIntervalType={getDisplayIntervalType(template.id)}
                                isDateInvalid={getMaintenanceDateInvalid(template.id)}
                                onRemove={() => onMaintenanceRemove(template.id)}
                                onDateChange={(value) => onMaintenanceDateChange(template.id, value)}
                                onDateBlur={() => onMaintenanceDateBlur(template.id)}
                                onOpenCostSettings={() => onOpenCostSettingsModal(template.id)}
                                onOpenIntervalSettings={() => onOpenIntervalSettingsModal(template.id)}
                                onEditTemplate={onOpenEditTemplateModal && (() => onOpenEditTemplateModal(template))}
                                onMouseEnter={() => setHighlightedActivity(`${componentKey}-${template.id}`)}
                                onMouseLeave={() => setHighlightedActivity(null)}
                                displayArea={getDisplayArea(template.id)}
                                componentQuantity={getComponentQuantity(template.id)}
                                customQuantity={getActivityCustomQuantity(template.id)}
                                costUnit={getTemplateCostUnit(template.id)}
                                hasCustomCost={getActivityHasCustomCost(template.id)}
                                onQuantityChange={(quantity) => onQuantityChange(template.id, quantity)}
                                onQuantityClear={() => onQuantityClear(template.id)}
                                comment={getActivityComment(template.id)}
                                canComment={hasActivityEntry(template.id)}
                                onCommentChange={(text) => onCommentChange(template.id, text)}
                                bulkPresence={bulk ? { ...bulk.rowPresence(template.id), onAddToAll: () => onAddRowToAll(template.id) } : undefined}
                            />
                        );
                    })}

                    {/* Kontroller: checks covering the component, always last */}
                    <CheckRows
                        rows={coveringChecks}
                        buildingKey={buildingKey}
                        disabled={isExcluded}
                        selectedCount={bulk ? bulk.keys.length : undefined}
                        onRemove={onRemoveCheck}
                        onEditDate={onEditCheckDate}
                    />

                    {/* New Activity Button */}
                    <div className="activity-row" style={{ borderTop: '1px dashed #dee2e6', backgroundColor: '#fdfdfd' }}>
                        <div className="d-flex align-items-center flex-wrap gap-2 p-2" style={{ paddingLeft: '32px' }}>
                            <button
                                className="btn btn-sm btn-primary d-flex align-items-center fw-medium px-2 py-1"
                                onClick={onOpenNewActivityModal}
                                disabled={isExcluded}
                                style={{ fontSize: '14px' }}
                            >
                                <Plus size={16} className="me-2" />
                                Ny aktivitet
                            </button>
                            <button
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center fw-medium px-2 py-1 component-check-add-button"
                                onClick={onOpenAddCheck}
                                disabled={isExcluded}
                                style={{ fontSize: '14px' }}
                            >
                                <Plus size={16} className="me-2" />
                                Kontroll
                            </button>
                            {/* Copying from another component is per component: not in Massredigering */}
                            {!bulk && (
                                // The span carries the tooltip: a disabled button shows none
                                <span
                                    title={hasCopyActivitiesSources ? undefined : 'Ingen annan komponent av samma slag har aktiviteter att kopiera'}
                                >
                                    <button
                                        className="btn btn-sm btn-outline-primary d-flex align-items-center fw-medium px-2 py-1"
                                        onClick={onOpenCopyActivitiesModal}
                                        disabled={isExcluded || !hasCopyActivitiesSources}
                                        style={{ fontSize: '14px' }}
                                    >
                                        <Copy size={16} className="me-2" />
                                        Kopiera alla aktiviteter från annan komponent
                                    </button>
                                </span>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Done Button - below the activities, where the work on a component ends */}
            {allComplete && (
                <div className="component-done-row">
                    <button
                        className={`btn done-component-btn ${isDone ? 'done' : ''}`}
                        onClick={onDoneToggle}
                        disabled={isExcluded}
                    >
                        <Check size={18} className="me-2" />
                        {doneLabel}
                    </button>
                    {bulk && !isDone && bulk.doneCount > 0 && (
                        <span className="text-muted small ms-3">{bulk.doneCount} av {bulk.keys.length} är redan klara</span>
                    )}
                </div>
            )}

            {/* Timeline */}
            {hasTimeline && lifetime && (
                <ComponentTimeline
                    componentKey={componentKey}
                    component={plan.component}
                    lifetime={lifetime}
                    templates={templates}
                    draftComponents={draftComponents}
                    building={building}
                    hasCondition={plan.hasCondition}
                    hasLifetime={plan.hasLifetime}
                    highlightedActivity={highlightedActivity}
                />
            )}

        </div>
    );
};
